import React from 'react';
import { Stack, Typography } from '@mui/material';
import { capitalizeFirstLetter } from 'formatters';

import { Details } from './Details';
import { EMPTY_VALUE, formatCurrencyText, formatEnumText, getChangeAction } from './lib';

interface Props {
  value: Record<string, any>;
}

const getTextValue = (value: string | null | undefined): string => value ?? EMPTY_VALUE;

const getEquipmentRows = (
  before: Record<string, any> | null | undefined,
  after: Record<string, any> | null | undefined
) => [
  {
    label: 'Stock Number',
    before: getTextValue(before?.stockNumber),
    after: getTextValue(after?.stockNumber),
    rawBefore: before?.stockNumber,
    rawAfter: after?.stockNumber,
  },
  {
    label: 'VIN',
    before: getTextValue(before?.vin),
    after: getTextValue(after?.vin),
    rawBefore: before?.vin,
    rawAfter: after?.vin,
  },
  {
    label: 'Cost',
    before: formatCurrencyText(before?.cost ?? null),
    after: formatCurrencyText(after?.cost ?? null),
    rawBefore: before?.cost,
    rawAfter: after?.cost,
  },
  {
    label: 'Wholesale Price',
    before: formatCurrencyText(before?.price?.wholesalePrice ?? null),
    after: formatCurrencyText(after?.price?.wholesalePrice ?? null),
    rawBefore: before?.price?.wholesalePrice,
    rawAfter: after?.price?.wholesalePrice,
  },
  {
    label: 'Markup',
    before: formatCurrencyText(before?.price?.markup ?? null),
    after: formatCurrencyText(after?.price?.markup ?? null),
    rawBefore: before?.price?.markup,
    rawAfter: after?.price?.markup,
  },
  {
    label: 'Markup Type',
    before: formatEnumText(before?.price?.markupType),
    after: formatEnumText(after?.price?.markupType),
    rawBefore: before?.price?.markupType,
    rawAfter: after?.price?.markupType,
  },
  {
    label: 'Retail Price',
    before: formatCurrencyText(before?.price?.retailPrice ?? null),
    after: formatCurrencyText(after?.price?.retailPrice ?? null),
    rawBefore: before?.price?.retailPrice,
    rawAfter: after?.price?.retailPrice,
  },
  {
    label: 'Discount',
    before: formatCurrencyText(before?.price?.discount ?? null),
    after: formatCurrencyText(after?.price?.discount ?? null),
    rawBefore: before?.price?.discount,
    rawAfter: after?.price?.discount,
  },
  {
    label: 'Discount Type',
    before: formatEnumText(before?.price?.discountType),
    after: formatEnumText(after?.price?.discountType),
    rawBefore: before?.price?.discountType,
    rawAfter: after?.price?.discountType,
  },
  {
    label: 'Net Price',
    before: formatCurrencyText(before?.price?.netPrice ?? null),
    after: formatCurrencyText(after?.price?.netPrice ?? null),
    rawBefore: before?.price?.netPrice,
    rawAfter: after?.price?.netPrice,
  },
  {
    label: 'Sales Tax',
    before: formatCurrencyText(before?.price?.salesTax ?? null),
    after: formatCurrencyText(after?.price?.salesTax ?? null),
    rawBefore: before?.price?.salesTax,
    rawAfter: after?.price?.salesTax,
  },
  {
    label: 'Sales Tax Type',
    before: formatEnumText(before?.price?.salesTaxType),
    after: formatEnumText(after?.price?.salesTaxType),
    rawBefore: before?.price?.salesTaxType,
    rawAfter: after?.price?.salesTaxType,
  },
  {
    label: 'Total',
    before: formatCurrencyText(before?.price?.total ?? null),
    after: formatCurrencyText(after?.price?.total ?? null),
    rawBefore: before?.price?.total,
    rawAfter: after?.price?.total,
  },
];

export const EquipmentChanges = ({ value }: Props) => {
  const { before, after } = value;

  const toArray = (items: Record<string, any> | Record<string, any>[] | null | undefined): Record<string, any>[] =>
    Array.isArray(items) ? items : items ? [items] : [];

  const beforeItems = toArray(before);
  const afterItems = toArray(after);

  const equipmentIds = Array.from(
    new Set([...beforeItems, ...afterItems].map((item, index) => item?.equipmentId ?? index))
  );

  const findById = (items: Record<string, any>[], id: unknown, index: number) =>
    items.find(item => (item?.equipmentId ?? null) === id) ?? items[index] ?? null;

  return (
    <Stack spacing={1}>
      {equipmentIds.map((id, index) => {
        const beforeItem = findById(beforeItems, id, index);
        const afterItem = findById(afterItems, id, index);
        const action = getChangeAction(beforeItem ?? null, afterItem ?? null);

        return (
          <Stack key={String(id)} spacing={0.5}>
            <Typography variant="body2" fontWeight={500}>
              {`${capitalizeFirstLetter(action)} equipment`}
            </Typography>

            <Details action={action} rows={getEquipmentRows(beforeItem, afterItem)} />
          </Stack>
        );
      })}
    </Stack>
  );
};
